import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTranslation } from 'react-i18next';
import { moduleRegistry } from './moduleRegistry';
import type { ModuleRoute } from './types';
import { useRoles } from '@/auth/useRoles';

const COLLAPSED_MODULES_KEY = 'allphi.navigation.collapsedModules.v2';
const allNavigationModules = moduleRegistry.filter((module) => module.id !== 'notifications');

/**
 * Determines which navigation modules should start collapsed, restoring the
 * user's last choice from local storage.
 *
 * Only a single module is allowed to be expanded at a time by design (an
 * accordion-style nav), so if the stored state has more than one expanded
 * module (e.g. from an older app version or manual tampering), it is
 * normalized here to keep only the first expanded module and persisted back.
 * @returns The set of module ids that should render collapsed.
 */
function getInitialCollapsedModules(): Set<string> {
  try {
    const storedValue = window.localStorage.getItem(COLLAPSED_MODULES_KEY);
    if (!storedValue) {
      return new Set(allNavigationModules.map((module) => module.id));
    }

    const storedModuleIds = JSON.parse(storedValue);
    const collapsed = new Set<string>(Array.isArray(storedModuleIds) ? storedModuleIds : []);
    const expandedModules = allNavigationModules.filter((module) => !collapsed.has(module.id));

    if (expandedModules.length <= 1) return collapsed;

    // More than one module was expanded (unexpected state) — collapse all but the first.
    const normalized = new Set(allNavigationModules.map((module) => module.id));
    normalized.delete(expandedModules[0].id);
    window.localStorage.setItem(COLLAPSED_MODULES_KEY, JSON.stringify([...normalized]));
    return normalized;
  } catch {
    // Local storage may be unavailable/corrupted (e.g. private browsing) — default to all collapsed.
    return new Set(allNavigationModules.map((module) => module.id));
  }
}

interface ModuleNavigationProps {
  pathname: string;
  onNavigate: (path: string) => void;
  compact?: boolean;
}

/**
 * Renders the sidebar navigation, grouping routes by module and filtering
 * modules the current user does not have access to. Note that this is a
 * display-only convenience: actual access is enforced server-side, not by
 * this component.
 * @param pathname - Current route path, used to highlight the active item.
 * @param onNavigate - Callback invoked with the target path when a route is clicked.
 * @param compact - Renders an icon-only, condensed layout when true.
 */
export function ModuleNavigation({ pathname, onNavigate, compact = false }: ModuleNavigationProps) {
  const { t } = useTranslation();
  const {
    assetsAccess,
    contractsAccess,
    notificationsAccess,
    mobilityAccess,
    mobilityReviewer,
    mobilityPayroll,
    mobilityAdmin,
    mobilityAuditor,
  } = useRoles();
  const mobilityManage = mobilityAccess || mobilityReviewer || mobilityPayroll || mobilityAdmin || mobilityAuditor;
  const [collapsedModules, setCollapsedModules] = useState(getInitialCollapsedModules);
  const navigationModules = allNavigationModules.filter((module) =>
    module.id === 'assets'
      ? assetsAccess
      : module.id === 'contracts'
        ? contractsAccess
          : module.id === 'mobility'
            ? mobilityManage
            : true);
  const notificationModule = notificationsAccess
    ? moduleRegistry.find((module) => module.id === 'notifications')
    : undefined;

  // Enforces single-module-expanded accordion behavior: expanding one module
  // collapses all others rather than toggling only the clicked module.
  const toggleModule = (moduleId: string) => {
    setCollapsedModules((current) => {
      const isOpening = current.has(moduleId);
      const next = new Set(allNavigationModules.map((module) => module.id));

      if (isOpening) next.delete(moduleId);

      window.localStorage.setItem(COLLAPSED_MODULES_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  // Recursively renders a route and its nested children, indenting by level.
  const renderRoute = (route: ModuleRoute, level = 0) => {
    const selected = pathname === route.path;

    const routeButton = (
      <ListItemButton
        aria-current={selected ? 'page' : undefined}
        selected={selected}
        onClick={() => onNavigate(route.path)}
        sx={(theme) => ({
          position: 'relative',
          minHeight: compact ? 44 : 46,
          mx: compact ? 0.25 : 1.5,
          mb: 0.5,
          pl: compact ? 0 : level === 0 ? 1.25 : 2.25,
          pr: compact ? 0 : 1.25,
          justifyContent: compact ? 'center' : 'flex-start',
          borderRadius: 1.5,
          color: 'text.secondary',
          transition: theme.transitions.create(
            ['background-color', 'color', 'transform'],
            { duration: theme.transitions.duration.shorter },
          ),
          '&::before': {
            position: 'absolute',
            top: 10,
            bottom: 10,
            left: 0,
            width: 3,
            borderRadius: '0 4px 4px 0',
            backgroundColor: 'primary.main',
            content: '""',
            opacity: 0,
          },
          '&:hover': {
            color: 'text.primary',
            backgroundColor: alpha(theme.palette.primary.main, 0.07),
            transform: compact ? 'none' : 'translateX(2px)',
          },
          '&.Mui-selected': {
            color: 'primary.main',
            backgroundColor: alpha(theme.palette.primary.main, 0.11),
            '&::before': { opacity: 1 },
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
            },
          },
        })}
      >
        <ListItemIcon
          sx={(theme) => ({
            minWidth: compact ? 0 : 38,
            justifyContent: 'center',
            color: 'inherit',
            '& .MuiSvgIcon-root': {
              fontSize: level === 0 ? 21 : 19,
              opacity: selected ? 1 : 0.82,
              transition: theme.transitions.create('opacity'),
            },
          })}
        >
          {route.icon}
        </ListItemIcon>
        {!compact && (
          <ListItemText
            primary={t(route.labelKey)}
            primaryTypographyProps={{
              fontSize: '0.925rem',
              lineHeight: 1.3,
              fontWeight: selected ? 650 : level === 0 ? 550 : 450,
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <Box key={route.path}>
        <Tooltip title={compact ? t(route.labelKey) : ''} placement="right" arrow>
          {routeButton}
        </Tooltip>

        {route.children?.map((child: ModuleRoute) =>
          renderRoute(child, level + 1)
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        p: compact ? 0.75 : 1.25,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1.25 }}>
        {navigationModules.map((module) => {
          const collapsed = collapsedModules.has(module.id);
          const expanded = !collapsed;
          const contentId = `navigation-module-${module.id}`;

          return (
            <Box
              component="section"
              key={module.id}
              sx={(theme) => ({
                overflow: 'hidden',
                border: '1px solid',
                borderColor: expanded
                  ? alpha(theme.palette.primary.main, 0.22)
                  : theme.palette.divider,
                borderRadius: compact ? 1.5 : 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                boxShadow: expanded
                  ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.07)}`
                  : '0 1px 3px rgba(0, 0, 0, 0.025)',
                transition: theme.transitions.create(['border-color', 'box-shadow']),
              })}
            >
              <Tooltip title={compact ? t(module.labelKey) : ''} placement="right" arrow>
                <ButtonBase
                  onClick={() => toggleModule(module.id)}
                  aria-expanded={!collapsed}
                  aria-controls={contentId}
                  aria-label={t(
                    collapsed ? 'app.expandNavigationModule' : 'app.collapseNavigationModule',
                    { module: t(module.labelKey) },
                  )}
                  sx={(theme) => ({
                  width: '100%',
                  minHeight: 52,
                  px: compact ? 0.5 : 1.25,
                  py: 1,
                  justifyContent: compact ? 'center' : 'flex-start',
                  textAlign: 'left',
                  color: expanded ? 'primary.main' : 'text.secondary',
                  backgroundColor: expanded
                    ? alpha(theme.palette.primary.main, 0.035)
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.065),
                  },
                  })}
                >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ minWidth: 0, flex: compact ? '0 0 auto' : 1, mr: compact ? 0 : 1 }}
                >
                  <Box
                    sx={(theme) => ({
                      display: 'grid',
                      placeItems: 'center',
                      width: compact ? 34 : 30,
                      height: compact ? 34 : 30,
                      borderRadius: 1,
                      flexShrink: 0,
                      color: expanded ? 'primary.main' : 'text.secondary',
                      backgroundColor: expanded
                        ? alpha(theme.palette.primary.main, 0.11)
                        : alpha(theme.palette.text.primary, 0.055),
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    })}
                  >
                    {module.icon}
                  </Box>
                  {!compact && <Typography
                    variant="overline"
                    color="text.secondary"
                    noWrap
                    sx={{
                      color: 'inherit',
                      fontSize: '0.7rem',
                      fontWeight: 750,
                      letterSpacing: '0.09em',
                      lineHeight: 1,
                      minWidth: 0,
                    }}
                  >
                    {t(module.labelKey)}
                  </Typography>}
                </Stack>
                {!compact && <KeyboardArrowDownIcon
                  sx={(theme) => ({
                    ml: 'auto',
                    flexShrink: 0,
                    fontSize: 20,
                    color: 'text.secondary',
                    transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform'),
                  })}
                />}
                </ButtonBase>
              </Tooltip>

              <Collapse in={!collapsed} timeout="auto">
                <List
                  id={contentId}
                  disablePadding
                  sx={{
                    pt: 0.25,
                    pb: 0.75,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {module.routes.map((route: ModuleRoute) => renderRoute(route))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {notificationModule && (
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Divider sx={{ mb: 1.25 }} />
          <List disablePadding aria-label={t(notificationModule.labelKey)}>
            {notificationModule.routes.map((route: ModuleRoute) => renderRoute(route))}
          </List>
        </Box>
      )}
      <List disablePadding sx={{ pt: 0.5 }}>
        {renderRoute({ labelKey: 'modules.administration', path: '/admin/access', icon: <AdminPanelSettingsIcon /> })}
      </List>
    </Box>
  );
}
